import {
  ClientStatus,
  ProducerLabel,
  Role,
} from "@ameelio/connect-call-client";
import { Box, Stack, useMediaQuery as measureScreenWidth } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import useChange from "@react-hook/change";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CallFeature, CallParticipant, Interval } from "../../api/graphql";
import blurBackgroundEnabled from "../../lib/blurBackgroundEnabled";
import featureEnabled from "../../lib/featureEnabled.ts";
import isAmeelioTablet from "../../lib/isAmeelioTablet";
import { largeTabletAndUp } from "../../lib/responsiveHelpers";
import { isConfidentialCall } from "../../lib/securityFeaturesUtils.ts";
import {
  DroppedStreams,
  useStreamTrackerContext,
} from "../../lib/StreamTrackerProvider";
import { DeviceInfo, useDeviceChanges } from "../../lib/useDevices";
import useMediaPermissions from "../../lib/useMediaPermissions";
import { darkPalette, ltrTheme } from "../../theme";
import CallBackgroundSelector from "./CallBackgroundSelector";
import CallChat from "./CallChat";
import CallConfidentialWidget from "./CallConfidentialWidget";
import CallControls from "./CallControls";
import CallPanel, { chatWidth } from "./CallPanel";
import CallTimerWidget from "./CallTimerWidget";
import CaptionBox from "./CaptionBox";
import CaptionsContainer from "./CaptionsContainer";
import FloatingSelfieVideo from "./FloatingSelfieVideo";
import ParticipantAudio from "./ParticipantAudio";
import { useSegmenterContext } from "./SegmenterProvider";
import StatusWidget from "./StatusWidget";
import { ConnectionInfo } from "./types/ConnectionInfo";
import CustomBackground from "./types/CustomBackground";
import useCallAlerts from "./useCallAlerts";
import useUnreadMessageCount from "./useUnreadMessageCount";
import VideoParticipant from "./VideoParticipant";

enum Panel {
  None,
  Chat,
  Participants,
  Backgrounds,
}

const canBlurBackground = blurBackgroundEnabled();

const backdropStyle: SystemStyleObject<Theme> = {
  px: { xs: 1, md: 2 },
  py: { xs: 1, md: 2 },
  height: "100%",
  backgroundColor: darkPalette.background.default,
  overflow: "hidden",
  position: "relative",
};

export type Props = {
  facilityName: string;

  // User
  currentParticipant: Pick<CallParticipant, "externalId" | "fullName">;

  features: CallFeature[];

  // All participants
  participants: Pick<CallParticipant, "externalId" | "fullName">[];

  // Connection information
  connectionInfo: ConnectionInfo;

  interval: Pick<Interval, "startAt" | "endAt">;
  onPeerJoined: () => void;
  onLeave: () => void;
  leaving?: boolean;
  beginMuted: boolean;
  beginHidden: boolean;

  canChooseBackground: boolean;
  canRemoveBackground: boolean;
  customBackground?: CustomBackground;
  onSetCustomBackground?: (c: CustomBackground) => void;
};

function VideoCallInterface({
  facilityName,
  connectionInfo,
  currentParticipant,
  participants,
  features,
  interval,
  onPeerJoined,
  onLeave,
  leaving,
  beginMuted,
  beginHidden,
  canChooseBackground,
  canRemoveBackground,
  customBackground,
  onSetCustomBackground,
}: Props) {
  const { t } = useTranslation();

  const participantsById = Object.fromEntries(
    participants.map((c) => [c.externalId, c])
  );

  const {
    messages,
    sendMessage,

    // Things only used in webinars:
    // remoteAudioMute,
    // remoteAudioUnmute,

    // Things both have:
    callStatus,
    clientStatus,

    localProducers,
    pauseProducer,
    resumeProducer,

    toggleCaption,

    logClientEvent,
    peers, // Currently only one peer allowed; future-proofed

    captionsTimeline,

    enableFrux,

    callFeatures,

    user: receivedUser,
  } = connectionInfo;

  useCallAlerts(messages);

  // Enable frux immediately
  useEffect(() => {
    if (enableFrux) enableFrux();
  }, [enableFrux]);

  const onDeviceAdded = useCallback(
    (device: DeviceInfo) => {
      if (clientStatus === ClientStatus.connected) {
        logClientEvent(`Device added: ${device.label} - ${device.kind}`);
      }
    },
    [logClientEvent, clientStatus]
  );
  const onDeviceRemoved = useCallback(
    (device: DeviceInfo) => {
      if (clientStatus === ClientStatus.connected) {
        logClientEvent(`Device removed: ${device.label} - ${device.kind}`);
      }
    },
    [logClientEvent, clientStatus]
  );
  // Handle device changes (e.g. unplugging a microphone)
  useDeviceChanges(onDeviceAdded, onDeviceRemoved);

  const { cameraDisabled, microphoneDisabled } = useMediaPermissions();
  useEffect(() => {
    if (clientStatus === ClientStatus.connected) {
      logClientEvent(
        `Device availability (status: ${clientStatus}) - Camera: ${!cameraDisabled} - Microphone: ${!microphoneDisabled}`
      );
    }
  }, [cameraDisabled, microphoneDisabled, clientStatus, logClientEvent]);

  const toggleAudio = useCallback(() => {
    if (localProducers.audio?.paused) resumeProducer(ProducerLabel.audio);
    else pauseProducer(ProducerLabel.audio);
  }, [pauseProducer, resumeProducer, localProducers]);

  const toggleVideo = useCallback(() => {
    if (localProducers.video?.paused) resumeProducer(ProducerLabel.video);
    else pauseProducer(ProducerLabel.video);
  }, [pauseProducer, resumeProducer, localProducers]);

  const nonMonitorPeers = Object.values(peers).filter(
    (p) => p.user.role !== Role.monitor
  );
  const peer = nonMonitorPeers[0];
  const isGridLayout =
    nonMonitorPeers.length >= 2 &&
    featureEnabled("VIDEO_MULTI_PARTICIPANT_LAYOUT");
  const gridColumns = nonMonitorPeers.length + 1 <= 4 ? 2 : 3;
  const streamTracker = useStreamTrackerContext();
  const [peerHasConnected, setPeerHasConnected] = useState<boolean>(false);
  const [shownPanel, setShownPanel] = useState<Panel>(Panel.None);
  const [showCaptions, setShowCaptions] = useState<boolean>(false);

  // Used for responsive layout
  const isLargeTabletAndUp = largeTabletAndUp(measureScreenWidth);

  // Track last known set of unjoined peers
  // and trigger onPeerJoined event when a new one appears.
  const knownPeers = useRef<Set<string>>(new Set());
  useEffect(() => {
    Object.keys(nonMonitorPeers).forEach((key) => {
      if (!knownPeers.current.has(key)) onPeerJoined();
    });
    knownPeers.current = new Set(Object.keys(nonMonitorPeers));
  }, [nonMonitorPeers, onPeerJoined]);

  const unreadMessageCount = useUnreadMessageCount(
    messages,
    shownPanel === Panel.Chat
  );

  useChange(
    receivedUser?.connectionState?.badConnection,
    (isDisabled: boolean | undefined): void => {
      if (isDisabled) {
        // Track a stream drop originating locally if video disabled
        streamTracker.handleDrop(DroppedStreams.local);
      }
    }
  );

  // Track a stream drop originating from peer if their connection state changes/video disabled
  useChange(
    peer?.connectionState?.badConnection,
    (isDisabled: boolean | undefined): void => {
      if (isDisabled) streamTracker.handleDrop(DroppedStreams.peer);
    }
  );

  const peerExists = !!peer;
  useEffect(() => {
    if (!peerHasConnected) {
      setPeerHasConnected(peerExists);
    }
  }, [peerExists, peerHasConnected]);

  const { status: segmenterStatus } = useSegmenterContext();

  const displaySelfMuted = localProducers.audio
    ? localProducers.audio.paused
    : beginMuted;
  const displaySelfHidden = localProducers.video
    ? localProducers.video.paused
    : beginHidden;

  return (
    <Box
      sx={{
        height: 1,
        width:
          shownPanel !== Panel.None && isLargeTabletAndUp
            ? `calc(100% - ${chatWidth})`
            : "100%",
      }}
    >
      <Box sx={backdropStyle}>
        {!isGridLayout && (
          <FloatingSelfieVideo
            user={currentParticipant}
            peerHasConnected={peerHasConnected}
            hidden={displaySelfHidden}
            muted={displaySelfMuted}
            localProducers={localProducers}
            callStatus={callStatus}
            clientStatus={clientStatus}
            connectionState={receivedUser?.connectionState}
          />
        )}
        {!isLargeTabletAndUp && (
          <Box
            sx={{
              position: "absolute",
              top: ltrTheme.spacing(3),
              right: ltrTheme.spacing(2),
              zIndex: "modal",
            }}
          >
            <Stack direction="row" spacing={2}>
              {isConfidentialCall(features) && (
                <CallConfidentialWidget features={features} />
              )}
              <CallTimerWidget
                startAt={interval.startAt}
                endAt={interval.endAt}
              />
            </Stack>
          </Box>
        )}
        <Box sx={{ width: 1, height: 1 }}>
          {isGridLayout ? (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                  gap: 1,
                  width: 1,
                  height: 1,
                }}
              >
                <VideoParticipant
                  origin="local"
                  mirrored
                  audioStream={localProducers.audio?.stream}
                  stream={localProducers.video?.stream}
                  videoPaused={displaySelfHidden}
                  audioMuted={displaySelfMuted}
                  user={currentParticipant}
                  callStatus={callStatus}
                  clientStatus={clientStatus}
                  connectionState={receivedUser?.connectionState}
                  containerSx={{ borderRadius: 2 }}
                />
                {nonMonitorPeers.map((p) => (
                  <VideoParticipant
                    key={p.user.id}
                    origin="remote"
                    audioStream={p.consumers.audio?.stream}
                    stream={p.consumers.video?.stream}
                    videoPaused={p.consumers.video?.paused}
                    audioMuted={p.consumers.audio?.paused}
                    user={participantsById[p.user.id]}
                    callStatus={callStatus}
                    clientStatus={clientStatus}
                    connectionState={p.connectionState}
                    containerSx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
              {nonMonitorPeers.map((p) => (
                <ParticipantAudio
                  key={p.user.id}
                  srcObject={p.consumers.audio?.stream}
                  autoPlay
                />
              ))}
            </>
          ) : (
            peerHasConnected &&
            peer && (
              <>
                <VideoParticipant
                  origin="remote"
                  audioStream={peer.consumers.audio?.stream}
                  stream={peer.consumers.video?.stream}
                  videoPaused={peer.consumers.video?.paused}
                  audioMuted={peer.consumers.audio?.paused}
                  user={participantsById[peer.user.id]}
                  callStatus={callStatus}
                  clientStatus={clientStatus}
                  connectionState={peer?.connectionState}
                  containerSx={{ borderRadius: 4 }}
                />
                <ParticipantAudio
                  srcObject={peer.consumers.audio?.stream}
                  autoPlay
                />
              </>
            )
          )}
        </Box>
        {clientStatus === "initializing" ? (
          <StatusWidget message={t("Connecting...")} />
        ) : clientStatus === "disconnected" ? (
          <StatusWidget
            message={t(
              "You are disconnected from the call. Please check your network connection."
            )}
          />
        ) : clientStatus === "errored" ? (
          <StatusWidget
            message={t(
              "An error occurred with the call. Please try leaving and rejoining."
            )}
          />
        ) : !peerHasConnected ? (
          <StatusWidget message={t("Waiting for others to join")} />
        ) : !peer ? (
          <StatusWidget message={t("Waiting for others to rejoin")} />
        ) : null}
        {showCaptions && captionsTimeline.length && (
          <CaptionsContainer>
            {captionsTimeline.map((segment) => (
              <CaptionBox
                key={segment.timestamp}
                user={
                  segment.user && participantsById[segment.user]
                    ? participantsById[segment.user]
                    : {
                        fullName: t("You"),
                      }
                }
                message={segment.text}
              />
            ))}
          </CaptionsContainer>
        )}
        <Box
          sx={{
            display: "flex",
            width: 1,
            position: "absolute",
            zIndex: "modal",
            left: 0,
            bottom: ltrTheme.spacing(8),
            px: 2,
            height: 0,
          }}
        >
          <Box
            sx={{
              flex: "1 1 0px",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {isLargeTabletAndUp && (
              <Stack direction="row" spacing={2}>
                <CallTimerWidget
                  startAt={interval.startAt}
                  endAt={interval.endAt}
                />
                {isConfidentialCall(features) && (
                  <CallConfidentialWidget features={features} />
                )}
              </Stack>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <CallControls
              muted={displaySelfMuted}
              hidden={displaySelfHidden}
              captions={showCaptions}
              captionsDisabled={clientStatus !== ClientStatus.connected}
              captionsAvailable={callFeatures && callFeatures.captions}
              alerts={false}
              onToggleAudio={toggleAudio}
              onToggleVideo={toggleVideo}
              onToggleCaptions={() => {
                if (toggleCaption) {
                  const newState = !showCaptions;
                  toggleCaption(newState).catch((e: unknown) => {
                    if (e instanceof Error && e.message !== "Not connected") {
                      Sentry.captureException(e);
                    }
                  });
                  setShowCaptions(newState);
                }
              }}
              backgroundSelectionDisabled={
                clientStatus !== ClientStatus.connected || !canChooseBackground
              }
              onToggleBackgroundSelection={
                segmenterStatus === "failed" ||
                !onSetCustomBackground ||
                isAmeelioTablet()
                  ? undefined
                  : () =>
                      setShownPanel(
                        shownPanel === Panel.Backgrounds
                          ? Panel.None
                          : Panel.Backgrounds
                      )
              }
              onToggleChat={
                callFeatures && callFeatures.chat
                  ? () =>
                      setShownPanel(
                        shownPanel === Panel.Chat ? Panel.None : Panel.Chat
                      )
                  : undefined
              }
              messageCount={unreadMessageCount}
              onLeave={onLeave}
              leaveCallLabel={t("Leave call")}
              leaveCallDisabled={
                leaving || clientStatus === ClientStatus.initializing
              }
              styleOverrides={{
                transform: "translateY(-50%)",
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 0px" }}>&nbsp;</Box>
        </Box>
      </Box>
      {shownPanel !== Panel.None && (
        <CallPanel>
          {shownPanel === Panel.Chat && (
            <CallChat
              callFeatures={features}
              facilityName={facilityName}
              currentParticipantId={currentParticipant.externalId}
              participants={participants}
              messages={messages}
              onSend={
                callStatus === "live" && clientStatus === ClientStatus.connected
                  ? async (message) => {
                      await sendMessage(message).catch((e: unknown) =>
                        Sentry.captureException(e)
                      );
                    }
                  : undefined
              }
              onClose={() => setShownPanel(Panel.None)}
            />
          )}
          {shownPanel === Panel.Backgrounds && onSetCustomBackground && (
            <CallBackgroundSelector
              value={customBackground || CustomBackground.NONE}
              onChange={(c: CustomBackground) => {
                onSetCustomBackground(c);
                logClientEvent(`Setting custom background: ${c.kind}`);
              }}
              onClose={() => setShownPanel(Panel.None)}
              canClearCustomBackground={canRemoveBackground}
              canBlurBackground={canBlurBackground} // SC-6218 Blur filter on a 2D canvas isn't currently supported in Safari
            />
          )}
        </CallPanel>
      )}
    </Box>
  );
}

export default VideoCallInterface;
